// Sprint 16 A4: Ease Score Gauge — circular, gradient stroke, 0-100
import { useId } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface EaseScoreGaugeProps {
  score: number;
  trend: "up" | "down" | "flat";
  change: number;
}

export function EaseScoreGauge({ score, trend, change }: EaseScoreGaugeProps) {
  const gradientId = useId();
  const circumference = 2 * Math.PI * 45; // radius 45
  const progress = (score / 100) * circumference;
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendColor = trend === "up" ? "text-[var(--color-success)]" : trend === "down" ? "text-[var(--color-destructive)]" : "text-[var(--color-muted-foreground)]";

  const trendLabel = trend === "up" ? "trending up" : trend === "down" ? "trending down" : "flat";

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg
          viewBox="0 0 100 100"
          className="w-full h-full -rotate-90"
          role="img"
          aria-label={`Ease Score: ${score} out of 100, ${trendLabel} ${Math.abs(change)} points`}
        >
          {/* Background circle */}
          <circle cx="50" cy="50" r="45" fill="none" stroke="var(--color-border)" strokeWidth="8" />
          {/* Progress circle with brand gradient */}
          <circle
            cx="50" cy="50" r="45" fill="none"
            stroke={`url(#${gradientId})`} strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            className="transition-all duration-1000 ease-out"
          />
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--color-primary)" />
              <stop offset="100%" stopColor="var(--color-ai)" />
            </linearGradient>
          </defs>
        </svg>
        {/* Score text in center */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-[var(--color-foreground)]">{score}</span>
          <span className="text-xs text-[var(--color-muted-foreground)]">Ease Score</span>
        </div>
      </div>
      {/* Trend */}
      <div className={`mt-2 flex items-center gap-1 text-sm font-medium ${trendColor}`}>
        <TrendIcon className="h-3.5 w-3.5" />
        <span>{change >= 0 ? "+" : ""}{change} from yesterday</span>
      </div>
    </div>
  );
}
